import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage';
import { TenantPasswordChangePage } from './pages/TenantPasswordChangePage';
import { SuperLayout } from './pages/super/SuperLayout';
import { SuperLoginPage } from './pages/super/SuperLoginPage';
import { SuperOrgsPage } from './pages/super/SuperOrgsPage';
import { SuperOrgDetailLayout } from './pages/super/SuperOrgDetailLayout';
import { SuperOrgConsumoPage, SuperOrgModelosPage, SuperOrgUsuariosPage } from './pages/super/SuperOrgDetailTabs';
import { SuperSpendPage } from './pages/super/SuperSpendPage';
import { SuperOrgPwaTokensPage } from './pages/super/SuperOrgPwaTokensPage';
import { SuperOrgBrandPage } from './pages/super/SuperOrgBrandPage';
import { SuperOrgRolesPage } from './pages/super/SuperOrgRolesPage';
import { SuperOrgTranscriptionPage } from './pages/super/SuperOrgTranscriptionPage';
import { AgentSelectorProvider } from './auth/AgentSelectorContext';
import { TenantPermissionsProvider } from './auth/TenantPermissionsContext';
import { RequirePermission } from './auth/RequirePermission';
import { SuperAiSettingsPage } from './pages/super/SuperAiSettingsPage';
import { SuperWizardAgentsPage } from './pages/super/SuperWizardAgentsPage';
import { SuperDemoMeetingsPage } from './pages/super/SuperDemoMeetingsPage';
import { SuperDemoUsagePage } from './pages/super/SuperDemoUsagePage';
import { SuperOrgDemoUsagePage } from './pages/super/SuperOrgDemoUsagePage';
import { ConsoleLayout } from './pages/console/ConsoleLayout';
import { DashboardPage } from './pages/console/DashboardPage';
import { AgentsPage } from './pages/console/AgentsPage';
import { IntegrationsPage } from './pages/console/IntegrationsPage';
import { ConversationsLayout } from './pages/console/ConversationsLayout';
import { ConversationChatPane } from './pages/console/ConversationChatPane';
import { ConversationsIndexPlaceholder } from './pages/console/ConversationsIndexPlaceholder';
import { WORKFLOWS_UI_ENABLED } from './config/productFeatures';
import { WorkflowsPage } from './pages/console/WorkflowsPage';
import { WorkflowEditorPage } from './pages/console/WorkflowEditorPage';
import { LearningsPage } from './pages/console/LearningsPage';
import { RulesPage } from './pages/console/RulesPage';
import { HandoffTriggersPage } from './pages/console/HandoffTriggersPage';
import { WhatsappTemplatesPage } from './pages/console/WhatsappTemplatesPage';
import { MessageAnalyticsPage } from './pages/console/MessageAnalyticsPage';
import { AgentPluginsPage } from './pages/console/AgentPluginsPage';
import { PluginsRedirect } from './pages/console/PluginsRedirect';
import { PluginConfigPage } from './pages/console/PluginConfigPage';
import { AgentAuditPage } from './pages/console/AgentAuditPage';
import { AuditRedirect } from './pages/console/AuditRedirect';
import { CalendarResourceTypesPage } from './pages/console/CalendarResourceTypesPage';
import { CalendarResourcesPage } from './pages/console/CalendarResourcesPage';
import { CalendarAvailabilityPage } from './pages/console/CalendarAvailabilityPage';
import { CalendarBookingsPage } from './pages/console/CalendarBookingsPage';
import { S3ConfigPage } from './pages/console/S3ConfigPage';
import { DemoConfigPage } from './pages/console/DemoConfigPage';
import { TestDemoPage } from './pages/console/TestDemoPage';
import { CalendarViewPage } from './pages/console/calendarView/CalendarViewPage';
import { ContactBlacklistPage } from './pages/console/ContactBlacklistPage';
import { AdsConnectorsPage } from './pages/console/AdsConnectorsPage';
import { StyleGuidePage } from './pages/console/StyleGuidePage';
import { NotificationSettingsPage } from './pages/console/NotificationSettingsPage';
import { BrandSettingsPage } from './pages/console/BrandSettingsPage';
import { OperatorNotificationsPage } from './pages/console/notifications/OperatorNotificationsPage';
import { DemoPage } from './pages/DemoPage';
import { GrowlProvider } from './ui/GrowlProvider';
import { DocsArticle } from './pages/docs/DocsArticle';
import { DocsHub } from './pages/docs/DocsHub';
import { DocsShell } from './pages/docs/DocsShell';

export function App() {
  return (
    <GrowlProvider>
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/demo/:token" element={<DemoPage />} />
      <Route path="/docs" element={<DocsShell variant="public" />}>
        <Route index element={<DocsHub variant="public" />} />
        <Route path=":area/:slug" element={<DocsArticle variant="public" />} />
      </Route>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/cambiar-contraseña" element={<TenantPasswordChangePage />} />
      <Route path="/super" element={<SuperLayout />}>
        <Route index element={<Navigate to="/super/orgs" replace />} />
        <Route path="login" element={<SuperLoginPage />} />
        <Route path="orgs" element={<Outlet />}>
          <Route index element={<SuperOrgsPage />} />
          <Route path=":orgId" element={<SuperOrgDetailLayout />}>
            <Route index element={<Navigate to="usuarios" replace />} />
            <Route path="usuarios" element={<SuperOrgUsuariosPage />} />
            <Route path="uso-demo" element={<SuperOrgDemoUsagePage />} />
            <Route path="consumo" element={<SuperOrgConsumoPage />} />
            <Route path="modelos" element={<SuperOrgModelosPage />} />
            <Route path="pwa" element={<SuperOrgPwaTokensPage />} />
            <Route path="marca" element={<SuperOrgBrandPage />} />
            <Route path="roles" element={<SuperOrgRolesPage />} />
            <Route path="transcripcion" element={<SuperOrgTranscriptionPage />} />
          </Route>
        </Route>
        <Route path="gastos" element={<SuperSpendPage />} />
        <Route path="ai" element={<SuperAiSettingsPage />} />
        <Route path="wizard-agents" element={<SuperWizardAgentsPage />} />
        <Route path="demo-meetings" element={<SuperDemoMeetingsPage />} />
        <Route path="demo-usage" element={<SuperDemoUsagePage />} />
      </Route>
      <Route
        path="/app"
        element={
          <TenantPermissionsProvider>
            <AgentSelectorProvider>
              <ConsoleLayout />
            </AgentSelectorProvider>
          </TenantPermissionsProvider>
        }
      >
        <Route index element={<RequirePermission><DashboardPage /></RequirePermission>} />
        <Route path="agents" element={<RequirePermission><AgentsPage /></RequirePermission>} />
        <Route path="plugins" element={<RequirePermission><PluginsRedirect /></RequirePermission>} />
        <Route path="audit" element={<RequirePermission><AuditRedirect /></RequirePermission>} />
        <Route path="agents/:agentId/plugins" element={<RequirePermission><AgentPluginsPage /></RequirePermission>} />
        <Route path="agents/:agentId/plugins/:pluginId/config" element={<RequirePermission><PluginConfigPage /></RequirePermission>} />
        <Route path="agents/:agentId/audit" element={<RequirePermission><AgentAuditPage /></RequirePermission>} />
        <Route path="agents/:agentId/calendar/resource-types" element={<RequirePermission><CalendarResourceTypesPage /></RequirePermission>} />
        <Route path="agents/:agentId/calendar/resources" element={<RequirePermission><CalendarResourcesPage /></RequirePermission>} />
        <Route path="agents/:agentId/calendar/resources/:resourceId/availability" element={<RequirePermission><CalendarAvailabilityPage /></RequirePermission>} />
        <Route path="agents/:agentId/calendar/bookings" element={<RequirePermission><CalendarBookingsPage /></RequirePermission>} />
        <Route path="agents/:agentId/s3/config" element={<RequirePermission><S3ConfigPage /></RequirePermission>} />
        <Route path="agents/:agentId/demo" element={<RequirePermission><DemoConfigPage /></RequirePermission>} />
        {WORKFLOWS_UI_ENABLED ? (
          <>
            <Route path="workflows" element={<RequirePermission><WorkflowsPage /></RequirePermission>} />
            <Route path="workflows/:id" element={<RequirePermission><WorkflowEditorPage /></RequirePermission>} />
          </>
        ) : (
          <>
            <Route path="workflows" element={<Navigate to="/app" replace />} />
            <Route path="workflows/:id" element={<Navigate to="/app" replace />} />
          </>
        )}
        <Route path="integrations" element={<RequirePermission><IntegrationsPage /></RequirePermission>} />
        <Route path="plantillas" element={<RequirePermission><WhatsappTemplatesPage /></RequirePermission>} />
        <Route path="notifications" element={<RequirePermission><OperatorNotificationsPage /></RequirePermission>} />
        <Route path="settings/notifications" element={<RequirePermission><NotificationSettingsPage /></RequirePermission>} />
        <Route path="settings/brand" element={<RequirePermission><BrandSettingsPage /></RequirePermission>} />
        <Route path="docs" element={<RequirePermission><DocsShell variant="console" /></RequirePermission>}>
          <Route index element={<DocsHub variant="console" />} />
          <Route path=":area/:slug" element={<DocsArticle variant="console" />} />
        </Route>
        <Route path="ai-settings" element={<Navigate to="/app" replace />} />
        <Route path="conversations" element={<RequirePermission><ConversationsLayout /></RequirePermission>}>
          <Route index element={<ConversationsIndexPlaceholder />} />
          <Route path=":id" element={<ConversationChatPane />} />
        </Route>
        <Route path="learnings" element={<RequirePermission><LearningsPage /></RequirePermission>} />
        <Route path="rules" element={<RequirePermission><RulesPage /></RequirePermission>} />
        <Route path="handoff" element={<RequirePermission><HandoffTriggersPage /></RequirePermission>} />
        <Route path="message-analytics" element={<RequirePermission><MessageAnalyticsPage /></RequirePermission>} />
        <Route path="calendario" element={<RequirePermission><CalendarViewPage /></RequirePermission>} />
        <Route path="lista-negra" element={<RequirePermission><ContactBlacklistPage /></RequirePermission>} />
        <Route path="conectores-ads" element={<RequirePermission><AdsConnectorsPage /></RequirePermission>} />
        <Route path="test" element={<RequirePermission><TestDemoPage /></RequirePermission>} />
        <Route path="style-guide" element={<RequirePermission><StyleGuidePage /></RequirePermission>} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </GrowlProvider>
  );
}
